<template>
  <section
    data-cy="property-details-view"
    :data-view-ready="!loading">
    <div
      v-if="isEditor || isJanitorEditor"
      class="floating-wrapper">
      <coozzy-button
        class="floating-button"
        design="transparent"
        data-cy="edit-property-floating-btn"
        @click="editClicked">
        <coozzy-edit-icon class="too-left" />
      </coozzy-button>
    </div>
    <div
      v-if="property !== null && property !== undefined"
      class="row">
      <div
        v-if="loading"
        class="col-12 text-center">
        <coozzy-spinner class="m-3" />
      </div>
      <div
        v-else
        class="col-12">
        <div class="row">
          <div class="col-12">
            <coozzy-page-title :title="$t('message.propertiesView.tabs.property') +': ' + propertyName" />
          </div>
        </div>

        <div class="row d-block d-md-none">
          <!-- Action buttons on mobile -->
          <div
            v-if="isEditor || isJanitorEditor"
            class="col-12">
            <coozzy-button
              design="primary"
              class="w-100 mb-2"
              data-cy="edit-property-btn"
              @click="editClicked">
              {{ $t('message.generic.edit') }}
            </coozzy-button>
          </div>
          <div class="col-12">
            <coozzy-button
              design="secondary"
              class="w-100 mb-2"
              data-cy="back-property-btn"
              @click="backClicked">
              {{ $t('message.generic.back') }}
            </coozzy-button>
          </div>
        </div>

        <div class="row">
          <div class="col-12 customTabs">
            <b-tabs
              v-model="tabIndex"
              no-key-nav
              class="mb-3">
              <b-tab
                :title="$t('message.propertiesView.tabs.property')"
                class="p-0">
                <template #title>
                  <router-link
                    data-cy="property-tab-property"
                    :to="getPropertyRoute('property')">
                    <span>{{ $t('message.propertiesView.tabs.property') }}</span>
                  </router-link>
                </template>
                <property-tab
                  :property-selected="property"
                  :read-only="true"
                  :expand-property-mortgage-id="expandPropertyMortgageId"
                  :expand-property-insurance-id="expandPropertyInsuranceId"
                  @selected-device-for-history="selectedDeviceForHistory" />
              </b-tab>
              <b-tab
                v-if="!isJanitor"
                class="p-0"
                :title="$t('message.propertiesView.tabs.landRegister')">
                <template #title>
                  <router-link
                    data-cy="property-tab-land-register"
                    :to="getPropertyRoute('landRegister')">
                    <span>{{ $t('message.propertiesView.tabs.landRegister') }}</span>
                  </router-link>
                </template>
                <land-register-tab
                  :property="property"
                  :read-only="true"
                  :element-selected="elementSelected"
                  @selected-land-register="selectedLandRegister" />
              </b-tab>
              <b-tab
                class="p-0"
                :title="$t('message.propertiesView.tabs.buildings')">
                <template #title>
                  <router-link
                    data-cy="property-tab-buildings"
                    :to="getPropertyRoute('buildings')">
                    <span>{{ $t('message.propertiesView.tabs.buildings') }}</span>
                  </router-link>
                </template>
                <buildings-tab
                  :property-selected="property"
                  :read-only="true" />
              </b-tab>
              <b-tab
                class="p-0"
                :title="$t('message.onBoarding.createBuilding.device')">
                <template #title>
                  <router-link
                    data-cy="property-tab-devices"
                    :to="getPropertyRoute('devices')">
                    <span>{{ $t('message.onBoarding.createBuilding.device') }}</span>
                  </router-link>
                </template>
                <devices-tab
                  ref="devicesTab"
                  :property="property"
                  :element-selected="elementSelected"
                  :add-new-item="addNewItem"
                  :read-only="true"
                  :device-filters="deviceFilters"
                  @selected-device="selectedDevice"
                  @selected-device-for-history="selectedDeviceForHistory"
                  @all-devices="deviceList"
                  @change-filter="updateWithFilter" />
              </b-tab>
              <b-tab
                v-if="isInvestPlanEnabled"
                class="p-0"
                :title="$t('message.onBoarding.investmentPlanning.title')">
                <template #title>
                  <router-link
                    :to="getPropertyRoute('investmentPlanning')">
                    <span>{{ $t('message.onBoarding.investmentPlanning.title') }}</span>
                  </router-link>
                </template>
                <invest-plan-tab
                  :read-only="true"
                  :invest-plan-filters="investPlanFilters"
                  :property="property"
                  @change-filter="updateWithFilter" />
              </b-tab>
              <b-tab
                v-if="(isOwnerModule && isCompanyAdmin) || !isOwnerModule"
                class="p-0"
                :title="$t('message.onBoardingTabs.contacts')">
                <template #title>
                  <router-link
                    data-cy="property-tab-contacts"
                    :to="getPropertyRoute('contacts')">
                    <span>{{ $t('message.onBoardingTabs.contacts') }}</span>
                  </router-link>
                </template>
                <contacts-tab
                  ref="contactsTab"
                  :property="property"
                  :read-only="true" />
              </b-tab>
              <b-tab
                class="p-0"
                :title="$t('message.onBoardingTabs.history')">
                <template #title>
                  <router-link
                    class="tab_title_color"
                    data-cy="property-tab-history"
                    :to="getPropertyRoute('history')">
                    <span>{{ $t('message.onBoardingTabs.history') }}</span>
                    <span
                      v-if="hasNotification"
                      class="small-dot" />
                  </router-link>
                </template>
                <history-tab
                  ref="historyTab"
                  :property="property"
                  :device-list="arrDeviceList"
                  :element-selected="elementSelected"
                  :route-name="$route.name"
                  :is-active="isHistoryTabActive"
                  :prev-route="prevRoute"
                  :default-text="defaultText"
                  :default-type="defaultType"
                  :default-creator="defaultCreator"
                  :default-assigne="defaultAssignee"
                  :default-priority="defaultPriority"
                  :default-status="defaultStatus"
                  :reference-ids="referenceIds"
                  :history-sub-tab="historySubTab"
                  @has-notification="updateHasNotification" />
              </b-tab>
              <b-tab
                v-if="isPortalLinkEnabled"
                class="p-0"
                :title="$t('message.portalTab.title')">
                <template #title>
                  <router-link
                    :to="getPropertyRoute('portalLinks')">
                    <span>{{ $t('message.portalTab.title') }}</span>
                  </router-link>
                </template>
                <portal-links-tab
                  ref="portalLinksTab"
                  :read-only="true"
                  :property="property" />
              </b-tab>
              <template #tabs-end>
                <b-nav-item
                  v-if="isEditor || isJanitorEditor"
                  href="#"
                  class="d-none d-md-block item-button">
                  <coozzy-button
                    design="primary"
                    class="nav-button nav-button-center"
                    data-cy="edit-property-btn"
                    @click="editClicked">
                    {{ $t('message.generic.edit') }}
                  </coozzy-button>
                </b-nav-item>
                <b-nav-item
                  href="#"
                  class="d-none d-md-block item-button">
                  <coozzy-button
                    design="secondary"
                    class="nav-button nav-button-right"
                    data-cy="back-property-btn"
                    @click="backClicked">
                    {{ $t('message.generic.back') }}
                  </coozzy-button>
                </b-nav-item>
              </template>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ObjectApi from '@/misc/apis/ObjectApi'
import AicApi from '@/misc/apis/AicApi'
import CoozzySpinner from '../framework/components/misc/CoozzySpinner'
import BuildingsTab from './tabs/BuildingsTab'
import LandRegisterTab from './tabs/LandRegisterTab'
import PropertyTab from './tabs/PropertyTab'
import DevicesTab from './tabs/DevicesTab'
import CoozzyButton from '../framework/components/button/CoozzyButton'
import Vue from 'vue'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle'
import { user } from '@/mixins/user'
import { routeChecks } from '@/mixins/routeChecks'
import HistoryTab from '@/property/tabs/HistoryTab.vue'
import ContactsTab from '@/property/tabs/ContactsTab.vue'
import InvestPlanTab from '@/property/tabs/InvestPlanTab.vue'
import PortalLinksTab from '@/property/tabs/PortalLinksTab.vue'
import PortalApi from '@/misc/apis/PortalApi'
import { mapMutations } from 'vuex'
import { investmentPlanning } from '@/mixins/investmentPlanning'

/** Algolia list URLs sync via history.pushState; Vue Router's `from` omits those query params. */
const PROPERTY_LIST_ROUTE_NAMES = new Set([
  'AdminPropertyView',
  'OwnerPropertyView',
  'AssetPropertyView',
  'AccountingPropertyView'
])

export default {
  name: 'PropertyDetailsView',
  components: {
    PortalLinksTab,
    InvestPlanTab,
    ContactsTab,
    CoozzyPageTitle,
    CoozzyButton,
    BuildingsTab,
    LandRegisterTab,
    PropertyTab,
    CoozzyEditIcon,
    CoozzySpinner,
    HistoryTab,
    DevicesTab
  },
  mixins: [user, routeChecks, investmentPlanning],
  beforeRouteUpdate(to, from, next) {
    this.prevRoute = to
    next()
  },
  beforeRouteLeave (to, from, next) {
    this.$store.dispatch('onboarding/setOpenedCollapses')
    this.prevRoute = from

    // The 500ms delay is necessary because the popstate event does not fire instantly
    setTimeout(() => {
      // AIC sync navigation fallback - Go back to AdminPropertyView
      const storedRoute = sessionStorage.getItem('AIC_NAV_SOURCE_ROUTE')
      if (storedRoute && this.browserBackClicked) {
        try {
          const route = JSON.parse(storedRoute)
          sessionStorage.removeItem('AIC_NAV_SOURCE_ROUTE')
          this.browserBackClicked = false
          next(route)
          return
        } catch (e) {
          sessionStorage.removeItem('AIC_NAV_SOURCE_ROUTE')
        }
      }

      next()
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (!from.name?.includes('PropertyDetailsView')) {
        vm.entryRoute = from
      }
    })
  },
  props: {
    id: {
      type: String,
      default: ''
    },
    view: {
      type: String,
      default: 'property'
    },
    elementSelected: {
      type: String,
      default: ''
    },
    addNewItem: {
      type: [Boolean, String],
      default: false
    },
    historyFilters: {
      type: String,
      default: ''
    },
    historySubTab: {
      type: String,
      default: 'tickets'
    },
    deviceFilters: {
      type: String,
      default: ''
    },
    investPlanFilters: {
      type: String,
      default: ''
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.propertiesView.tabs.property') + ' ' + this.propertyName
    }
  },
  data() {
    return {
      arrDeviceList: [],
      tabIndex: 0,
      property: null,
      hasNotification: false,
      expandPropertyMortgageId: '',
      expandPropertyInsuranceId: '',
      isPortalLinkEnabled: false,
      loading: false,
      isInvestPlanEnabled: false,
      prevRoute: this.$route,
      defaultText: '',
      defaultPriority: '',
      defaultType: '',
      referenceIds: '',
      defaultAssignee: '',
      defaultCreator: '',
      defaultStatus: '',
      entryRoute: null
    }
  },
  computed: {
    propertyName() {
      return this.property && this.property.name ? this.property.name : ''
    },
    isHistoryTabActive() {
      return this.$route.query.view === 'history'
    }
  },
  watch: {
    view: function () {
      if (this.isInvestPlanEnabled !== null) {
        this.openSelectedTabs()
      }
    },
    tabIndex: function (newVal) {
      if (newVal === 3) {
        this.$refs.devicesTab?.setFocus()
      }
    },
    isInvestPlanEnabled: function () {
      this.openSelectedTabs()
    }
  },
  mounted() {
    this.loading = true
    this.loadData()

    // Scroll to mortgage collapse in property tab
    if (localStorage.getItem('expandPropertyMortgageId')) {
      this.expandPropertyMortgageId = localStorage.getItem('expandPropertyMortgageId')
      localStorage.removeItem('expandPropertyMortgageId')
    }
    // Scroll to insurance collapse in property tab
    if (localStorage.getItem('expandPropertyInsuranceId')) {
      this.expandPropertyInsuranceId = localStorage.getItem('expandPropertyInsuranceId')
      localStorage.removeItem('expandPropertyInsuranceId')
    }
    if (this.historyFilters !== '') {
      const objFilter = JSON.parse(this.historyFilters)
      this.defaultText = objFilter.defaultText || ''
      this.defaultType = objFilter.defaultType || ''
      this.defaultStatus = objFilter.defaultStatus || ''
      this.defaultCreator = objFilter.defaultCreator || ''
      this.defaultAssigne = objFilter.defaultAssigne || ''
      this.defaultPriority = objFilter.defaultPriority || ''
      this.referenceIds = objFilter.referenceIds || ''
    }
    this.browserBackClicked = false
    window.onpopstate = () => {
      this.browserBackClicked = true
    }
  },
  beforeDestroy() {
    window.onpopstate = null
  },
  methods: {
    ...mapMutations('onboarding', ['setIsPortalLinkEnabled']),
    updateWithFilter(newVal, field) {
      const query = {
        elementSelected: this.elementSelected,
        addNewItem: this.addNewItem,
        view: this.view,
        deviceFilters: this.deviceFilters,
        historyFilters: this.historyFilters,
        historySubTab: this.historySubTab,
        investPlanFilters: this.investPlanFilters
      }
      const initQuery = JSON.stringify(query)
      query[field] = newVal
      if (initQuery !== JSON.stringify(query)) {
        if (this.isOwnerModule) {
          this.$router.push({ name: 'OwnerPropertyDetailsView', params: { id: this.id }, query: query })
        } else if (this.isAdminModule) {
          this.$router.push({ name: 'AdminPropertyDetailsView', params: { id: this.id }, query: query })
        } else if (this.isAssetModule) {
          this.$router.push({ name: 'AssetPropertyDetailsView', params: { id: this.id }, query: query })
        } else if (this.isAccountingModule) {
          this.$router.push({ name: 'AccountingPropertyDetailsView', params: { id: this.id }, query: query })
        }
      }
    },
    async loadData() {
      try {
        const responseProperty = await ObjectApi.getPropertyListByIds([this.id])
        this.property = responseProperty.properties[0]
        if (this.property.id) {
          AicApi.getSyncModeForProperty(this.property.id)
            .then(response => {
              this.property.aicSyncMode = response.syncModes
            })
            .catch(error => {
              console.error('Error getting sync mode for property:', error)
            })
        }

        const responsePortal = await PortalApi.getPortalStatus(this.property.ownerId)
        this.isPortalLinkEnabled = PortalApi.isPropertyPortalEnabled(responsePortal, this.property.id)
        this.setIsPortalLinkEnabled(this.isPortalLinkEnabled)
        if (this.property.buildingIds.length > 0) {
          this.isInvestPlanEnabled = await this.getBuildingEnabledInvestPlan(this.property.buildingIds)
        }
        if (this.isInvestPlanEnabled !== null) {
          this.openSelectedTabs()
        }
        this.loading = false
      } catch (e) {
        this.loading = false
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
      }
    },
    updateHasNotification(value) {
      this.hasNotification = value
    },
    deviceList(devices) {
      this.arrDeviceList = devices
    },
    selectedDevice(device, newVal, field) {
      this.updateElementSelected(device, '', newVal, field)
    },
    selectedLandRegister(plot) {
      this.updateElementSelected(plot)
    },
    selectedDeviceForHistory(his) {
      this.updateElementSelected(his, 'history')
    },
    getPropertyRoute(view) {
      if (this.isOwnerModule) {
        return { name: 'OwnerPropertyDetailsView', params: { id: this.id }, query: { view: view } }
      } else if (this.isAdminModule) {
        return { name: 'AdminPropertyDetailsView', params: { id: this.id }, query: { view: view } }
      } else if (this.isAssetModule) {
        return { name: 'AssetPropertyDetailsView', params: { id: this.id }, query: { view: view } }
      } else if (this.isAccountingModule) {
        return { name: 'AccountingPropertyDetailsView', params: { id: this.id }, query: { view: view } }
      }
    },
    updateElementSelected(item, view = '', newVal = false, field = '') {
      let query = null
      if (newVal) {
        query = { elementSelected: item.id, addNewItem: this.addNewItem, view: this.view, deviceFilters: this.deviceFilters, historyFilters: this.historyFilters }
        query[field] = newVal
      }
      if (item && item.id && (this.elementSelected !== item.id || (view !== '' && this.elementSelected === item.id && this.$route.query.view !== view))) {
        this.$router.replace({
          name: this.$route.name,
          params: { id: this.id },
          query: newVal && query ? query : { view: view || this.$route.query.view, elementSelected: item.id }
        })
        if (view === 'history') {
          this.tabIndex = this.isInvestPlanEnabled ? 6 : 5
        }
      }
    },
    backClicked() {
      // AIC sync navigation fallback - Go back to AdminPropertyView
      const storedRoute = sessionStorage.getItem('AIC_NAV_SOURCE_ROUTE')
      if (storedRoute) {
        try {
          const route = JSON.parse(storedRoute)
          sessionStorage.removeItem('AIC_NAV_SOURCE_ROUTE')
          this.$router.push(route)
          return
        } catch (e) {
          sessionStorage.removeItem('AIC_NAV_SOURCE_ROUTE')
        }
      }

      if (this.entryRoute && this.entryRoute.name) {
        if (PROPERTY_LIST_ROUTE_NAMES.has(this.entryRoute.name)) {
          this.$router.go(-1)
        } else {
          this.$router.push(this.entryRoute)
        }
      } else {
        this.$router.go(-1)
      }
    },
    editClicked() {
      localStorage.setItem('scrollpos', window.scrollY)
      if (this.$parent.$route.name === 'AdminPropertyDetailsView') {
        this.$router.push({
          name: 'AdminPropertyEditingView',
          params: { id: this.id },
          query: {
            view: this.view,
            elementSelected: this.elementSelected,
            historySubTab: this.historySubTab,
            deviceFilters: this.deviceFilters,
            investPlanFilters: this.investPlanFilters
          }
        })
      } else if (this.$parent.$route.name === 'AssetPropertyDetailsView') {
        this.$router.push({
          name: 'AssetPropertyEditingView',
          params: { id: this.id },
          query: {
            view: this.view,
            elementSelected: this.elementSelected,
            historySubTab: this.historySubTab,
            deviceFilters: this.deviceFilters,
            investPlanFilters: this.investPlanFilters
          }
        })
      } else if (this.$parent.$route.name === 'AccountingPropertyDetailsView') {
        this.$router.push({
          name: 'AccountingPropertyEditingView',
          params: { id: this.id },
          query: {
            view: this.view,
            elementSelected: this.elementSelected,
            historySubTab: this.historySubTab,
            deviceFilters: this.deviceFilters,
            investPlanFilters: this.investPlanFilters
          }
        })
      } else {
        this.$router.push({
          name: 'OwnerPropertyEditingView',
          params: { id: this.id },
          query: {
            view: this.view,
            elementSelected: this.elementSelected,
            historySubTab: this.historySubTab,
            deviceFilters: this.deviceFilters,
            investPlanFilters: this.investPlanFilters
          }
        })
      }
    },
    openSelectedTabs(routeView = '') {
      const view = routeView !== '' ? routeView : this.view
      if (view === 'property') {
        this.tabIndex = 0
      } else if (view === 'landRegister') {
        this.tabIndex = 1
      } else if (view === 'buildings') {
        this.tabIndex = 2
      } else if (view === 'devices') {
        this.tabIndex = 3
      } else if (view === 'investmentPlanning' && this.isInvestPlanEnabled) {
        this.tabIndex = 4
      } else if (view === 'contacts') {
        this.tabIndex = this.isInvestPlanEnabled ? 5 : 4
      } else if (view === 'history') {
        this.tabIndex = this.isInvestPlanEnabled ? 6 : 5
      } else if (view === 'portalLinks') {
        this.tabIndex = this.isInvestPlanEnabled ? 7 : 6
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.item {
  cursor: pointer;
}

:deep(.details-row-bg-1) {
  background-color: $color-table-row-2;
}

:deep(.details-row-bg-2) {
  background-color: $color-table-row-1;
}

:deep(label) {
  width: 100%;
}

:deep(.is-disabled) {
  .sort button{
    display: block !important;
  }

  .multiselect, .custom-checkbox {
    pointer-events: none;

    .multiselect__select, .multiselect__placeholder {
      display: none;
    }
  }

  .filter-inputs .multiselect, .custom-checkbox {
    pointer-events: auto !important;

    .multiselect__select, .multiselect__placeholder {
      display: block !important;
    }
  }

  .basic {
    input {
      opacity: 1;
      border: thin solid #ced4da;
      -webkit-box-shadow: inherit;
      box-shadow: inherit;
      margin-bottom: 0;
      pointer-events: initial;
      padding-left: 0.2rem;
      background: none;
    }
  }

  .multi-select-value {
    line-height: 1.5;
  }
}

:deep(.collapse-title) {
  &.collapsed {
    h5 {
      background-image: url('../assets/arrow_down.svg');
    }
  }

  h5 {
    background-image: url('../assets/arrow_up.svg');
    background-repeat: no-repeat;
    font-size: large;
    font-weight: 500 !important;
    height: 1.2rem;
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
    background-position: right;
  }
}

:deep(.global-card) {
  margin-bottom: 0;

  & > .card-body {
    -webkit-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    -moz-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
  }

  border-left: 0;
  border-right: 0;
  border-bottom: 0;
}

:deep(.global-card) {
  margin-bottom: 0;

  & > .card-body {
    -webkit-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    -moz-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
  }

  border-left: 0;
  border-right: 0;
  border-bottom: 0;

  &.no-top-border {
    border-top: 0;
  }
}
.floating-wrapper{
  right: 5vw;
  bottom: 4vh;
  z-index: 9999;
  position: fixed;
  .floating-button{
    border-radius: 100px !important;
    display: flex;
    align-items: center;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.20), 0 4px 4px rgba(0, 0, 0, 0.15);
    z-index: 2;
    justify-content: center;
    background-color: $color-primary-dark;
    padding: 20px !important;
    cursor: pointer;
    svg{
      color: white;
      font-size: 15px;
    }
  }
}
:deep(.tabs .nav-tabs .nav-link a.router-link-active), :deep(.tabs .nav-tabs .nav-link a) {
    color: inherit !important;
    text-decoration: none !important;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation]) {
    margin-right: 0.2rem;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item > a.nav-link) {
    margin-right: 0;
    padding: 0;
    box-shadow: none;
    border: 0;
    z-index: -1;
    height: 26.5px;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link.active > a) {
    font-weight: bold;
    background-color: #fff;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link:hover > a) {
    font-weight: bold;
    cursor: pointer;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link > a) {
    color: rgb(0, 68, 95) !important;
    border-color: #c8ced3 !important;
    border: 1px solid;
    border-radius: 0 !important;
    border-bottom: 0 !important;
    margin-right: 0;
    padding: 0.55rem 1.5rem;
    font-weight: 300;
    -webkit-box-shadow: inset 0 16px 20px -15px rgb(238 238 238), 3px 0 2px -2px #ccc;
    box-shadow: inset 0 16px 20px -15px rgb(238 238 238), 3px 0 2px -2px #ccc;
    cursor: default;
    height: 36.25px;
  }
  :deep(.card.shadow) {
    box-shadow: none !important;
  }
</style>
